import {
  FOLLOWUP_MODE_SOURCE,
  findConditionRuleProblem,
  isConditionAllowedInScope,
  isConditionSourceCustomFieldType,
  isOptionFieldType,
  isProductAssessmentSource,
  isQuizResultSource,
  isScoreConditionSource,
  isValueRequiredOperator,
  menuConditionOperator,
  operatorForNewSource,
  QUIZ_RESULT_SOURCE_PREFIX,
  scalarConditionOperators,
} from "@repo/shared";
import type {
  ConfiguredAssessmentType,
  FlowQuizSummary,
  PdfAttachmentConditionalDisplay,
  PdfConditionalOperator,
  PdfTemplateAttachment,
} from "@repo/shared/types";
import { ArrowDown, ArrowUp, FileText, SlidersHorizontal, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  assessmentSourceValueOptions,
  buildAssessmentPickerEntries,
  buildQuizScorePickerEntries,
  quizResultValueOptions,
  scoreSourcePassScore,
} from "@/components/conditional-display/assessment-picker-entries";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { ScalarOperatorItems } from "@/components/conditional-display/scalar-operator-items";
import { ScoreConditionValueInput } from "@/components/conditional-display/score-condition-value-input";
import { useCountryOptions } from "@/components/country-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/hooks/use-confirm";
import {
  useDeletePdfAttachment,
  usePdfAttachments,
  useReorderPdfAttachments,
  useUpdatePdfAttachment,
  useUploadPdfAttachment,
} from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { PdfFlowField } from "@/lib/types/pdf-template";
import type { ClientCustomField } from "./component-forms";
import { T } from "./pdf-template-attachments.i18n";

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

type FieldOption = string | { label: string; value: string };

const getOptionValue = (option: FieldOption): string =>
  typeof option === "string" ? option : option.value;
const getOptionLabel = (option: FieldOption): string =>
  typeof option === "string" ? option : option.label;

type PdfTemplateAttachmentsProps = {
  templateId: string;
  flowFields: PdfFlowField[];
  /** QL-13 — the `quiz` components on this template's flow. */
  quizzes?: FlowQuizSummary[];
  /** QL-13 — while true the quiz list is a placeholder (loading or failed), which must not read as "all dangling". */
  quizzesUnknown?: boolean;
  /** QL-361 — the configured assessment types of this template's flow. */
  configuredAssessmentTypes?: ConfiguredAssessmentType[];
  /** QL-361 — while true the list is a placeholder (loading or failed), which must not read as "all stale". */
  assessmentTypesUnknown?: boolean;
  clientCustomFields?: ClientCustomField[];
};

export function PdfTemplateAttachments({
  templateId,
  flowFields,
  quizzes = [],
  quizzesUnknown = false,
  configuredAssessmentTypes = [],
  assessmentTypesUnknown = false,
  clientCustomFields = [],
}: PdfTemplateAttachmentsProps) {
  const t = useT(T);
  const { confirm, ConfirmationDialog } = useConfirm();

  const { attachments } = usePdfAttachments(templateId);
  const uploadMutation = useUploadPdfAttachment();
  const updateMutation = useUpdatePdfAttachment();
  const deleteMutation = useDeletePdfAttachment();
  const reorderMutation = useReorderPdfAttachments();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [conditionAttachment, setConditionAttachment] = useState<PdfTemplateAttachment | null>(
    null,
  );

  // Attachments append once per PDF, so only instance-wide (global) sources apply —
  // same predicate as the section pickers and the server guard (QL-161).
  const globalFlowFields = flowFields.filter((f) => isConditionAllowedInScope(f.scope, "global"));
  // QL-254 — every client custom field type but "user", same as the other pickers.
  const conditionCustomFields = clientCustomFields.filter(
    (f) => f.isActive && isConditionSourceCustomFieldType(f.type),
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error(t.error, { description: t.invalidFileType });
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      toast.error(t.error, { description: t.fileTooLarge });
      return;
    }

    try {
      await uploadMutation.mutateAsync({ templateId, file });
      toast.success(t.attachmentUploaded);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.uploadFailed,
      });
    }
  };

  const handleDelete = async (attachment: PdfTemplateAttachment) => {
    const confirmed = await confirm({
      title: t.deleteAttachment,
      description: t.deleteConfirmation(attachment.fileName),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync({ templateId, attachmentId: attachment.id });
      toast.success(t.attachmentDeleted);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.deleteFailed,
      });
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const ids = attachments.map((a) => a.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];

    try {
      await reorderMutation.mutateAsync({ templateId, attachmentIds: ids });
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.reorderFailed,
      });
    }
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-lg font-medium">{t.attachments}</h3>
            <p className="text-sm text-muted-foreground">{t.attachmentsDescription}</p>
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="gap-2 shrink-0"
            loading={uploadMutation.isPending}
          >
            <Upload className="w-4 h-4" /> {t.uploadAttachment}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t.noAttachments}</p>
        ) : (
          <div className="space-y-2">
            {attachments.map((attachment, index) => (
              <div
                key={attachment.id}
                className="flex items-center gap-3 p-3 border rounded-md bg-card"
              >
                <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{attachment.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {attachment.pageCount} {attachment.pageCount === 1 ? t.page : t.pages} ·{" "}
                    {formatFileSize(attachment.fileSize)}
                  </p>
                </div>
                {attachment.conditionalDisplay?.enabled && (
                  <Badge variant="outline" className="shrink-0">
                    {t.conditional}
                  </Badge>
                )}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.moveUp}
                    disabled={index === 0 || reorderMutation.isPending}
                    onClick={() => handleMove(index, -1)}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.moveDown}
                    disabled={index === attachments.length - 1 || reorderMutation.isPending}
                    onClick={() => handleMove(index, 1)}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.editCondition}
                    onClick={() => setConditionAttachment(attachment)}
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.deleteAttachment}
                    disabled={deleteMutation.isPending}
                    onClick={() => handleDelete(attachment)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {conditionAttachment && (
        <AttachmentConditionDialog
          attachment={conditionAttachment}
          flowFields={globalFlowFields}
          clientCustomFields={conditionCustomFields}
          quizzes={quizzes}
          quizzesUnknown={quizzesUnknown}
          configuredAssessmentTypes={configuredAssessmentTypes}
          assessmentTypesUnknown={assessmentTypesUnknown}
          isSaving={updateMutation.isPending}
          onClose={() => setConditionAttachment(null)}
          onSave={async (conditionalDisplay) => {
            try {
              await updateMutation.mutateAsync({
                templateId,
                attachmentId: conditionAttachment.id,
                data: { conditionalDisplay },
              });
              toast.success(t.conditionSaved);
              setConditionAttachment(null);
            } catch (err) {
              toast.error(t.error, {
                description: err instanceof Error ? err.message : t.saveFailed,
              });
            }
          }}
        />
      )}

      <ConfirmationDialog />
    </Card>
  );
}

type AttachmentConditionDialogProps = {
  attachment: PdfTemplateAttachment;
  flowFields: PdfFlowField[];
  clientCustomFields: ClientCustomField[];
  quizzes: FlowQuizSummary[];
  quizzesUnknown: boolean;
  configuredAssessmentTypes: ConfiguredAssessmentType[];
  assessmentTypesUnknown: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (conditionalDisplay: PdfAttachmentConditionalDisplay | null) => void;
};

function AttachmentConditionDialog({
  attachment,
  flowFields,
  clientCustomFields,
  // QL-13 — quiz results are global sources; an attachment is always global.
  quizzes: activeQuizzes,
  quizzesUnknown,
  // QL-361 — so are the product-assessment statuses.
  configuredAssessmentTypes,
  assessmentTypesUnknown,
  isSaving,
  onClose,
  onSave,
}: AttachmentConditionDialogProps) {
  const { userLanguage, tenantDefaultLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const countryOptions = useCountryOptions();

  const existing = attachment.conditionalDisplay;
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [sourceFieldId, setSourceFieldId] = useState(existing?.sourceFieldId ?? "");
  const [storedOperator, setOperator] = useState<PdfConditionalOperator>(
    existing?.operator ?? "equals",
  );
  const [conditionValue, setConditionValue] = useState(existing?.value ?? "");
  const [error, setError] = useState<string | null>(null);

  const getFieldOptions = (fieldId: string): FieldOption[] => {
    if (fieldId === "__flow_status__") {
      return [
        { label: "Submitted", value: "submitted" },
        { label: "Cancelled", value: "cancelled" },
      ];
    }
    // QL-774 — the PDF builder seeds "true" / "false" for every instance.
    if (fieldId === FOLLOWUP_MODE_SOURCE) {
      return [
        { label: t.followupModeYes, value: "true" },
        { label: t.followupModeNo, value: "false" },
      ];
    }
    if (fieldId === "__classification_type__") {
      return ["Retail", "Professional", "Eligible Counterparty"];
    }
    if (fieldId === "__client_country__") {
      // QL-323 — values are the ISO codes the client record holds (a name
      // could never equal `client.country`); labels localized and sorted with
      // the Nordic markets first, the same list the client form uses.
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.code,
        label: o.label,
      }));
    }
    if (fieldId === "__client_type__") {
      return [
        { label: "Person", value: "Person" },
        { label: "Company", value: "Company" },
        { label: "Group", value: "Group" },
      ];
    }
    // QL-68 — Sumsub verified status shares the Trapets three-value shape.
    if (
      fieldId === "__trapets_pep__" ||
      fieldId === "__trapets_sanctioned__" ||
      fieldId === "__sumsub_verified__"
    ) {
      return [
        { label: "Unchecked", value: "unchecked" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    }
    // QL-68 — Sumsub document country: same localised list as Client Country,
    // valued by the alpha-3 code Sumsub reports (raw comparison, no table).
    if (fieldId === "__sumsub_document_country__") {
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.alpha3,
        label: o.label,
      }));
    }
    // QL-13 — the three quiz statuses; rules compare the raw status string.
    if (isQuizResultSource(fieldId)) {
      return quizResultValueOptions(userLanguage);
    }
    // QL-361 — Passed / Not passed / Not answered, or Incomplete for Combined.
    if (isProductAssessmentSource(fieldId)) {
      return assessmentSourceValueOptions(fieldId, userLanguage);
    }
    const booleanOptions: FieldOption[] = [
      { label: "Yes", value: "true" },
      { label: "No", value: "false" },
    ];
    if (fieldId.startsWith("__custom_field_")) {
      const customField = clientCustomFields.find(
        (f) => f.id === fieldId.replace("__custom_field_", ""),
      );
      if (customField?.type === "boolean") return booleanOptions;
      if (customField && isOptionFieldType(customField.type) && customField.options) {
        // Rule values store the option id (QL-123); show the localized label.
        return customField.options
          .filter((o) => !o.isArchived || o.id === conditionValue)
          .map((o) => ({
            value: o.id,
            label: localize(o.labelI18n) || o.id,
          }));
      }
      return [];
    }
    const field = flowFields.find((f) => f.id === fieldId);
    if (field?.type === "boolean") return booleanOptions;
    if (!field || !isOptionFieldType(field.type)) return [];
    // Rule values store the option id (QL-201); show the label. Archived
    // options stay listed only while the rule still uses them.
    return (field.options ?? [])
      .filter((o) => !o.isArchived || o.id === conditionValue)
      .map((o) => ({ value: o.id, label: o.label }));
  };

  // Keep the operator valid for the selected source.
  const handleSourceFieldChange = (newFieldId: string) => {
    setSourceFieldId(newFieldId);
    setConditionValue("");
    const newCustomField = newFieldId.startsWith("__custom_field_")
      ? clientCustomFields.find((f) => f.id === newFieldId.replace("__custom_field_", ""))
      : undefined;
    const fieldType =
      newCustomField?.type ?? flowFields.find((f) => f.id === newFieldId)?.type ?? "";
    // The attachment picker never offers the goal-type source, so the set
    // operators (`in`) it maps to cannot come back here.
    setOperator(
      operatorForNewSource(operator, { sourceId: newFieldId, fieldType }) as PdfConditionalOperator,
    );
  };

  const sourceCustomField = sourceFieldId.startsWith("__custom_field_")
    ? clientCustomFields.find((f) => f.id === sourceFieldId.replace("__custom_field_", ""))
    : undefined;
  const sourceField = flowFields.find((f) => f.id === sourceFieldId);
  const sourceFieldType = sourceCustomField?.type ?? sourceField?.type ?? "";

  const isMultiselectField = sourceFieldType === "multiselect";
  // QL-361 — score sources compare a number: Equals / Greater than / Less than
  // only, no "is empty" pair ("has the client answered" is a status question).
  const isScoreSource = isScoreConditionSource(sourceFieldId);

  const isEnumField =
    sourceFieldId.startsWith("__") ||
    (!!sourceCustomField && isOptionFieldType(sourceCustomField.type)) ||
    sourceFieldType === "boolean";
  // A score source gets its own menu even when an enum rule above covers it.
  const scalarOperators = scalarConditionOperators(sourceFieldId, isEnumField, sourceFieldType);
  // QL-237 / QL-254 — a stored rule whose operator the menu no longer lists
  // shows, and saves, the one a switch to its source would pick.
  const operator = isMultiselectField
    ? storedOperator
    : (menuConditionOperator(storedOperator, scalarOperators, {
        sourceId: sourceFieldId,
        fieldType: sourceFieldType,
      }) as PdfConditionalOperator);

  const showValueInput = isMultiselectField
    ? operator === "contains" || operator === "not_contains"
    : scalarOperators.includes(operator) && isValueRequiredOperator(operator);
  const isDropdownField =
    isOptionFieldType(sourceField?.type ?? "") || sourceField?.type === "boolean" || isEnumField;
  const dropdownOptions = sourceFieldId ? getFieldOptions(sourceFieldId) : [];

  const handleSave = () => {
    setError(null);
    if (!enabled) {
      onSave(null);
      return;
    }
    const ruleProblem = findConditionRuleProblem(
      { sourceFieldId, operator, value: conditionValue },
      {
        quizzes: quizzesUnknown ? null : activeQuizzes,
        assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
      },
    );
    if (ruleProblem) {
      setError(conditionRuleProblemMessage(ruleProblem, userLanguage));
      return;
    }
    onSave({ enabled: true, sourceFieldId, operator, value: conditionValue });
  };

  // QL-323 — the source picker is searchable: a flat option list with the same
  // entries, in the same order, the grouped Select used to render.
  const sourceOptions: SearchableSelectOption[] = [
    { value: "__flow_status__", label: t.flowStatus },
    { value: FOLLOWUP_MODE_SOURCE, label: t.followupMode },
    { value: "__client_country__", label: t.clientCountry },
    { value: "__client_type__", label: t.clientType },
    { value: "__classification_type__", label: t.classificationType },
    { value: "__trapets_pep__", label: t.trapetsPep },
    { value: "__trapets_sanctioned__", label: t.trapetsSanctioned },
    { value: "__sumsub_verified__", label: t.sumsubVerified },
    { value: "__sumsub_document_country__", label: t.sumsubDocumentCountry },
    // QL-13 — quiz results (global scope)
    ...activeQuizzes.map((quiz) => ({
      value: `${QUIZ_RESULT_SOURCE_PREFIX}${quiz.id}`,
      label: `${t.quizField} ${quiz.name}`,
    })),
    // QL-361 — quiz scores, one per quiz (global scope)
    ...buildQuizScorePickerEntries(activeQuizzes, userLanguage).map((entry) => ({
      value: entry.id,
      label: entry.label,
    })),
    ...clientCustomFields.map((field) => ({
      value: `__custom_field_${field.id}`,
      label: `${t.clientField} ${field.name} (${field.type})`,
    })),
    ...flowFields.map((field) => ({
      value: field.id,
      label: `${t.flowField} ${field.name} (${field.type})`,
    })),
    // QL-361 — product-assessment statuses and scores (global scope). Offered last: it is the
    // only grouped run, and SearchableSelect never closes a heading, so any ungrouped entry
    // after it would render under "Product assessment".
    ...buildAssessmentPickerEntries(configuredAssessmentTypes, {
      uiLanguage: userLanguage,
      nameLanguage: userLanguage,
      fallbackLanguage: tenantDefaultLanguage,
      valueLanguage: userLanguage,
    }).map((entry) => ({ value: entry.id, label: entry.label, group: entry.group })),
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t.conditionalInclusion}</DialogTitle>
          <DialogDescription>{attachment.fileName}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="attachment-condition-enabled">{t.enableCondition}</Label>
              <p className="text-sm text-muted-foreground">{t.conditionalInclusionDescription}</p>
            </div>
            <Switch
              id="attachment-condition-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          {enabled && (
            <div className="pl-4 space-y-4 border-l-2">
              <div className="space-y-2">
                <Label htmlFor="attachment-source-field">
                  {t.when} <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect
                  id="attachment-source-field"
                  value={sourceFieldId}
                  onValueChange={handleSourceFieldChange}
                  options={sourceOptions}
                  placeholder={t.selectField}
                  searchPlaceholder={t.searchFields}
                  emptyMessage={t.noFieldsAvailable}
                  noResultsMessage={t.noFieldsMatch}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="attachment-operator">{t.condition}</Label>
                <Select
                  value={operator}
                  onValueChange={(v) => setOperator(v as PdfConditionalOperator)}
                >
                  <SelectTrigger id="attachment-operator">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isMultiselectField ? (
                      <>
                        <SelectItem value="contains">{t.contains}</SelectItem>
                        <SelectItem value="not_contains">{t.notContains}</SelectItem>
                        <SelectItem value="is_empty">{t.isEmpty}</SelectItem>
                        <SelectItem value="is_not_empty">{t.isNotEmpty}</SelectItem>
                      </>
                    ) : (
                      <ScalarOperatorItems operators={scalarOperators} />
                    )}
                  </SelectContent>
                </Select>
              </div>

              {showValueInput && (
                <div className="space-y-2">
                  <Label htmlFor="attachment-condition-value">
                    {t.value} <span className="text-destructive">*</span>
                  </Label>
                  {isScoreSource ? (
                    <ScoreConditionValueInput
                      id="attachment-condition-value"
                      value={conditionValue}
                      onChange={setConditionValue}
                      passScore={scoreSourcePassScore(
                        sourceFieldId,
                        configuredAssessmentTypes,
                        activeQuizzes,
                      )}
                      language={userLanguage}
                    />
                  ) : sourceFieldId === "__client_country__" ||
                    sourceFieldId === "__sumsub_document_country__" ? (
                    <SearchableSelect
                      id="attachment-condition-value"
                      value={conditionValue}
                      onValueChange={setConditionValue}
                      options={dropdownOptions.map((option) => ({
                        value: getOptionValue(option),
                        label: getOptionLabel(option),
                      }))}
                      placeholder={t.selectValue}
                      searchPlaceholder={t.searchCountries}
                      noResultsMessage={t.noCountriesMatch}
                    />
                  ) : isDropdownField && dropdownOptions.length > 0 ? (
                    <Select value={conditionValue} onValueChange={setConditionValue}>
                      <SelectTrigger id="attachment-condition-value">
                        <SelectValue placeholder={t.selectValue} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {dropdownOptions.map((option) => (
                          <SelectItem key={getOptionValue(option)} value={getOptionValue(option)}>
                            {getOptionLabel(option)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : sourceFieldType === "date" ? (
                    // QL-209 — same picker as the date answer, so the value is yyyy-MM-dd
                    <DatePicker
                      id="attachment-condition-value"
                      value={conditionValue}
                      onChange={setConditionValue}
                    />
                  ) : sourceFieldType === "datetime" ? (
                    // QL-211 — same picker as the datetime answer, so the value is UTC
                    <DateTimePicker
                      id="attachment-condition-value"
                      value={conditionValue}
                      onChange={setConditionValue}
                    />
                  ) : (
                    <Input
                      id="attachment-condition-value"
                      value={conditionValue}
                      onChange={(e) => setConditionValue(e.target.value)}
                      placeholder={t.enterValue}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogCancel disabled={isSaving}>{t.cancel}</DialogCancel>
          <Button onClick={handleSave} loading={isSaving}>
            {t.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
